import React, { useCallback, useEffect } from 'react';
import { forTemplate } from '@trello/i18n';
import { BillingDates, ProductFeatures } from '@trello/product-features';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { CloseIcon } from '@trello/nachos/icons/close';
import { CloseButton } from 'app/src/components/CloseButton';
import { RouterLink } from 'app/src/components/RouterLink';
import { getBannerMessageName } from './getBannerMessageName';
import styles from './OverduePaymentBanner.less';

const format = forTemplate('header_overdue_banner');

interface OverduePaidAccount {
  standing: number;
  products: number[];
  billingDates?: BillingDates | null;
}

interface OverduePaymentBannerProps {
  id: string;
  name: string;
  displayName: string;
  paidAccount: OverduePaidAccount;
  onDismiss: (bannerName: string) => void;
}

export const OverduePaymentBanner: React.FC<OverduePaymentBannerProps> = ({
  id,
  name,
  displayName,
  paidAccount,
  onDismiss,
}) => {
  useEffect(() => {
    Analytics.sendViewedBannerEvent({
      bannerName: 'overdueBanner',
      source: getScreenFromUrl(),
      containers: {
        organization: { id },
      },
      attributes: {
        isTeamAdmin: true,
      },
    });
  }, [id]);

  const bannerName = getBannerMessageName(id, paidAccount);

  const onClickBillingLink = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'updateBillingInfoLink',
      source: 'overdueBanner',
      containers: {
        organization: { id },
      },
      attributes: {
        isTeamAdmin: true,
      },
    });
  }, [id]);

  const dismissBanner = useCallback(
    (e) => {
      e.preventDefault();
      Analytics.sendDismissedComponentEvent({
        componentType: 'banner',
        componentName: 'overdueBanner',
        source: getScreenFromUrl(),
        containers: {
          organization: { id },
        },
        attributes: {
          isTeamAdmin: true,
        },
      });

      onDismiss(bannerName);
    },
    [onDismiss, id, bannerName],
  );

  return (
    <div className={styles.warningBanner}>
      <CloseButton
        className={styles.close}
        onClick={dismissBanner}
        closeIcon={<CloseIcon size="small" color="quiet" />}
      />

      <div className={styles.bannerMessage}>
        {ProductFeatures.isStandardProduct(paidAccount?.products?.[0])
          ? format('payment-failed-standard', {
              team: <span key="org-name">{displayName}</span>,
            })
          : format('payment-failed-premium', {
              team: <span key="org-name">{displayName}</span>,
            })}{' '}
        <RouterLink
          href={`/${name}/billing`}
          className={styles.billingLink}
          onClick={onClickBillingLink}
        >
          {format('update-billing-info')}
        </RouterLink>
      </div>
    </div>
  );
};
